import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["form", "status", "feedback"]

  connect() {
    this.dirty = this.element.dataset.newsEditorInvalid === "true"
    this.busy = false
    this.currentUrl = window.location.href
    this.beforeUnload = this.beforeUnload.bind(this)
    this.popState = this.popState.bind(this)
    this.navigate = this.navigate.bind(this)
    window.addEventListener("beforeunload", this.beforeUnload)
    window.addEventListener("popstate", this.popState)
    this.element.addEventListener("click", this.navigate, true)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnload)
    window.removeEventListener("popstate", this.popState)
    this.element.removeEventListener("click", this.navigate, true)
  }

  changed(event) {
    if (!this.hasFormTarget || !this.formTarget.contains(event.target)) return
    this.dirty = true
    this.statusTarget.textContent = "Nesaglabātas izmaiņas"
  }

  submit(event) {
    if (event.defaultPrevented) return
    const form = event.target
    const editing = this.hasFormTarget && form === this.formTarget
    if (!editing && !this.confirmLeave()) {
      event.preventDefault()
      return
    }
    event.preventDefault()
    event.stopPropagation()
    if (this.busy) return
    const data = new FormData(form)
    if (form.method.toLowerCase() === "get") {
      const url = new URL(form.action)
      url.search = new URLSearchParams(data).toString()
      this.load(url.href)
    } else {
      this.save(form, data, editing)
    }
  }

  async save(form, data, editing) {
    this.setBusy(true, editing ? "Saglabā…" : "Dzēš ziņu…")
    try {
      if (editing) await this.uploadFiles(form, data)
      await this.request(form.action, { method: "POST", body: data }, true)
    } catch (error) {
      this.failed("Izmaiņas nav saglabātas. Pārbaudi savienojumu un mēģini vēlreiz.")
    }
  }

  async uploadFiles(form, data) {
    for (const input of form.querySelectorAll('input[type="file"][data-direct-upload-url]')) {
      if (input.files.length === 0) {
        const uploaded = data.getAll(input.name).filter(value => typeof value === "string" && value.length > 0)
        data.delete(input.name)
        uploaded.forEach(value => data.append(input.name, value))
        continue
      }
      data.delete(input.name)
      for (const file of input.files) {
        this.feedbackTarget.textContent = "Augšupielādē failus…"
        const blob = await new Promise((resolve, reject) => {
          new DirectUpload(file, input.dataset.directUploadUrl).create((error, result) => {
            if (error) reject(error)
            else resolve(result)
          })
        })
        data.append(input.name, blob.signed_id)
      }
    }
  }

  uploadFailed() {
    this.failed("Faila augšupielāde neizdevās. Izmaiņas nav saglabātas.")
  }

  navigate(event) {
    const link = event.target.closest("a[href]")
    if (!link || link.target === "_blank" || link.hasAttribute("download") || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
    if (!this.confirmLeave()) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    const url = new URL(link.href)
    if (url.origin !== window.location.origin || !/^\/blog_items(?:\/new|\/\d+\/edit)?$/.test(url.pathname)) return
    event.preventDefault()
    event.stopImmediatePropagation()
    if (!this.busy) this.load(url.href)
  }

  async load(url, push = true) {
    this.setBusy(true, "Ielādē…")
    try {
      await this.request(url, {}, push)
    } catch (error) {
      this.failed("Neizdevās ielādēt ziņas. Pārbaudi savienojumu un mēģini vēlreiz.")
      history.replaceState(null, "", this.currentUrl)
    }
  }

  async request(url, options, push) {
    const token = document.querySelector('meta[name="csrf-token"]')
    const headers = { "X-News-Workspace": "true" }
    if (token) headers["X-CSRF-Token"] = token.content
    const response = await fetch(url, {
      ...options,
      credentials: "same-origin",
      headers
    })
    if (!response.ok && response.status !== 422) throw new Error("Request failed")
    const documentFragment = new DOMParser().parseFromString(await response.text(), "text/html")
    const workspace = documentFragment.querySelector(".news-workspace")
    if (!workspace) {
      // Authentication redirects and non-workspace destinations use ordinary navigation.
      this.dirty = false
      window.location.assign(response.url)
      return
    }
    if (response.ok) this.dirty = false
    if (response.status !== 422 && push && response.url !== window.location.href) history.pushState(null, "", response.url)
    this.element.replaceWith(workspace)
    const focus = workspace.querySelector(response.status === 422 ? '[role="alert"]' : "#news-editor-heading")
    if (focus) {
      focus.setAttribute("tabindex", "-1")
      focus.focus({ preventScroll: true })
    }
  }

  popState() {
    if (this.busy || !this.confirmLeave()) {
      history.pushState(null, "", this.currentUrl)
      return
    }
    this.load(window.location.href, false)
  }

  setBusy(busy, message = "") {
    this.busy = busy
    for (const child of this.element.children) child.inert = busy && child !== this.feedbackTarget
    this.element.setAttribute("aria-busy", busy.toString())
    this.feedbackTarget.textContent = message
  }

  failed(message) {
    this.setBusy(false, message)
    this.feedbackTarget.setAttribute("role", "alert")
  }

  confirmLeave() {
    if (!this.dirty) return true
    return window.confirm("Ir nesaglabātas izmaiņas. Atstāt ziņu bez saglabāšanas?")
  }

  beforeUnload(event) {
    if (!this.dirty && !this.busy) return
    event.preventDefault()
    event.returnValue = ""
  }
}
